import React, { useMemo, useState } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { IAnomaliesV2SeriesPoint } from '../../shared/types'
import { varClass } from '../../shared'
import { formatDecimal, formatDkkLarge, robustYMax } from '../shared'

const xTickFormatter = (s: string) => {
  try {
    return format(parseISO(s), 'MMM d')
  } catch {
    return s
  }
}

const dkkAxisTick = (v: number) => {
  if (Math.abs(v) >= 1000) return `${formatDecimal(v / 1000, 0)}k`
  return formatDecimal(v, 0)
}

// Commercial context — sale and commission DKK side-by-side. Commission
// stopping while sales continue is the canonical "tracking broken" pattern,
// so the two lines belong on the same chart.
export const CommercialChart = (props: {
  series: IAnomaliesV2SeriesPoint[]
}) => {
  const { series } = props
  const [scale, setScale] = useState<'normal' | 'full'>('normal')

  const chartData = useMemo(
    () =>
      series.map((p) => ({
        date: p.eventDate,
        sale: p.saleDkk ?? null,
        commission: p.commissionDkk ?? null,
      })),
    [series]
  )

  const saleMax =
    scale === 'normal' ? robustYMax(chartData.map((r) => r.sale)) : null
  const commissionMax =
    scale === 'normal' ? robustYMax(chartData.map((r) => r.commission)) : null
  const isClipped =
    scale === 'normal' &&
    chartData.some(
      (r) =>
        (saleMax != null && r.sale != null && r.sale > saleMax) ||
        (commissionMax != null &&
          r.commission != null &&
          r.commission > commissionMax)
    )

  return (
    <div className="card anomalies-chart">
      <div className="card-body p-t-1">
        <div className="row row-space-between row-center m-b-3">
          <div className="text-dark text-bolder">Commercial context</div>
          <div className="row row-center" style={{ gap: 12, flexWrap: 'wrap' }}>
            <div className="anomalies-chart-legend">
              <span className="anomalies-chart-legend-item">
                <span
                  className="anomalies-chart-legend-line"
                  style={{ borderTopColor: '#4F9EEC' }}
                />
                Sale (DKK)
              </span>
              <span className="anomalies-chart-legend-item">
                <span
                  className="anomalies-chart-legend-line"
                  style={{ borderTopColor: '#A675F5' }}
                />
                Commission (DKK)
              </span>
            </div>
            <div className="row row-center" style={{ gap: 8 }}>
              <span className="text-light text-small">Scale:</span>
              <div className="anomalies-trend-toggle-group">
                <button
                  type="button"
                  className={varClass({
                    'anomalies-trend-toggle': true,
                    'active': scale === 'normal',
                  })}
                  onClick={() => setScale('normal')}
                >
                  Normal
                </button>
                <button
                  type="button"
                  className={varClass({
                    'anomalies-trend-toggle': true,
                    'active': scale === 'full',
                  })}
                  onClick={() => setScale('full')}
                >
                  Full
                </button>
              </div>
            </div>
          </div>
        </div>
        <ResponsiveContainer width="99.9%" height={220}>
          <ComposedChart
            data={chartData}
            margin={{ top: 8, left: 0, right: 0, bottom: 0 }}
          >
            <CartesianGrid
              horizontal
              vertical={false}
              strokeDasharray="6"
              stroke="#EDEFF2"
            />
            <Tooltip content={<CommercialTooltip />} />
            <Line
              yAxisId="sale"
              dataKey="sale"
              stroke="#4F9EEC"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
            <Line
              yAxisId="commission"
              dataKey="commission"
              stroke="#A675F5"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              tickFormatter={xTickFormatter}
              tick={{ fontSize: 11, fill: '#828B9E' }}
            />
            <YAxis
              yAxisId="sale"
              axisLine={false}
              tickLine={false}
              width={56}
              tickFormatter={dkkAxisTick}
              tick={{ fontSize: 11, fill: '#828B9E' }}
              domain={[0, saleMax != null ? saleMax * 1.05 : 'auto']}
              allowDataOverflow
            />
            <YAxis
              yAxisId="commission"
              orientation="right"
              axisLine={false}
              tickLine={false}
              width={48}
              tickFormatter={dkkAxisTick}
              tick={{ fontSize: 11, fill: '#828B9E' }}
              domain={[0, commissionMax != null ? commissionMax * 1.05 : 'auto']}
              allowDataOverflow
            />
          </ComposedChart>
        </ResponsiveContainer>
        {isClipped && (
          <div className="text-light text-small m-t-1 anomalies-chart-clip-note">
            Peak values clipped — switch to Full to see them.
          </div>
        )}
      </div>
    </div>
  )
}

const CommercialTooltip = (props: any) => {
  const { active, payload, label } = props
  if (!active || !payload || !payload.length) return null
  const row = payload[0]?.payload
  if (!row) return null
  return (
    <div className="anomalies-chart-tooltip">
      <div className="anomalies-chart-tooltip-date">
        {label ? format(parseISO(label), 'MMM d, yyyy') : ''}
      </div>
      <div className="anomalies-chart-tooltip-row">
        <span
          className="anomalies-chart-tooltip-dot"
          style={{ background: '#4F9EEC' }}
        />
        <span className="anomalies-chart-tooltip-label">Sale</span>
        <span className="anomalies-chart-tooltip-value">
          {formatDkkLarge(row.sale)}
        </span>
      </div>
      <div className="anomalies-chart-tooltip-row">
        <span
          className="anomalies-chart-tooltip-dot"
          style={{ background: '#A675F5' }}
        />
        <span className="anomalies-chart-tooltip-label">Commission</span>
        <span className="anomalies-chart-tooltip-value">
          {formatDkkLarge(row.commission)}
        </span>
      </div>
    </div>
  )
}
